import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Trash2, Settings, Users, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AdminClans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: clans, isLoading } = useQuery({
    queryKey: ["admin_clans"],
    queryFn: async () => {
      const res = await api.get("/clans/admin/all");
      return res.data.clans;
    },
    enabled: !!user && ["admin", "root"].includes(user.role)
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await api.delete(`/clans/admin/${id}`);
    },
    onSuccess: () => {
      toast.success("Clan deleted successfully");
      queryClient.invalidateQueries(["admin_clans"]);
    },
    onError: (err) => {
      toast.error(err.response?.data?.detail || "Failed to delete clan");
    }
  });

  const handleDelete = (id, name) => {
    if (window.confirm(`Are you sure you want to delete clan "${name}"? This action cannot be undone.`)) {
      deleteMutation.mutate(id);
    }
  };

  // Protect route
  if (!user || !["admin", "root"].includes(user.role)) {
    return <div className="text-center py-20 text-white">Access Denied</div>;
  }

  return (
    <div className="idg-container py-8 max-w-7xl mx-auto">
      <Link to="/admin" className="inline-flex items-center gap-2 text-gray-400 hover:text-white bg-black/40 px-4 py-2 rounded-lg border border-white/10 mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        <span className="font-bold text-sm uppercase tracking-wider">Înapoi la Listă Admin</span>
      </Link>

      <div className="bg-[#0d111a] border border-white/10 p-6 rounded-2xl mb-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600/10 to-transparent z-0"></div>
        <div className="relative z-10 flex items-center gap-3 mb-2">
          <Settings className="w-6 h-6 text-white" />
          <h1 className="text-2xl font-black text-white uppercase tracking-wider">Panou de Administrare Generală Clanuri</h1>
        </div>
        <p className="text-gray-400 text-sm relative z-10">
          Ești logat ca administrator pe forum. Ai acces să editezi, ștergi și să gestionezi membrii oricărui clan.
        </p>
      </div>

      <div className="bg-[#0d111a] border border-white/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-black/40 border-b border-white/10 text-xs text-gray-400 font-bold uppercase tracking-wider">
                <th className="py-4 px-6">Nume Clan</th>
                <th className="py-4 px-6">Tag</th>
                <th className="py-4 px-6">Lider</th>
                <th className="py-4 px-6">Membri</th>
                <th className="py-4 px-6">Recrutare</th>
                <th className="py-4 px-6">Acțiuni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {isLoading ? (
                <tr><td colSpan="6" className="text-center py-10"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full mx-auto"></div></td></tr>
              ) : clans?.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-10 text-gray-500">Nu există clanuri create.</td></tr>
              ) : (
                clans?.map(clan => (
                  <tr key={clan.id} className="hover:bg-white/[0.02] transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <img src={clan.logo_url} alt="" className="w-8 h-8 rounded-lg border border-white/10 bg-black" />
                        <Link to={`/clans/${clan.id}`} className="text-white font-bold hover:text-red-500">{clan.name}</Link>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className="bg-white/10 border border-white/5 text-gray-300 text-[10px] font-bold px-2 py-1 rounded uppercase">[{clan.tag}]</span>
                    </td>
                    <td className="py-4 px-6 text-gray-300 font-medium">
                      {clan.leader_name}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1.5 text-gray-300 text-sm">
                        <Users className="w-4 h-4 text-[#a855f7]" />
                        <span>{clan.member_count} / {clan.max_members || 100}</span>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      {clan.is_recruiting ? (
                        <span className="text-green-500 font-medium text-sm">Da</span>
                      ) : (
                        <span className="text-red-500 font-medium text-sm">Nu</span>
                      )}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2">
                        <Link to={`/clans/${clan.id}`} className="bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1">
                          <Settings className="w-3.5 h-3.5" /> Edit
                        </Link>
                        <Link to={`/clans/${clan.id}`} className="bg-white/5 hover:bg-white/10 text-gray-300 border border-white/10 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1">
                          <Users className="w-3.5 h-3.5" /> Membri
                        </Link>
                        <button 
                          onClick={() => handleDelete(clan.id, clan.name)}
                          disabled={deleteMutation.isPending}
                          className="bg-red-500/20 hover:bg-red-500/40 text-red-500 border border-red-500/30 px-3 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Șterge
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
